/**
 * The sky's geometry — where everything is, as pure arithmetic.
 *
 * One field of 344 units.  The sun sits at the centre; the rings are
 * concentric orbits spread evenly between the innermost and outermost
 * radius; the disc is cut into SECTOR wedges, one per family, sized so
 * no ring is crowded, and a family's worlds sit in its wedge on every
 * ring — so a column reads as one family, ring by ring, and a ring
 * reads as one standing, family by family.  The top of the disc is left
 * clear for the gate labels.
 *
 * Nothing here reads learner state or draws anything.  It answers
 * "where" for the orrery, the label engine and the cameras, and it is
 * what the tests hold the drawing to.
 */

export const FIELD = 344;
export const CENTRE = FIELD / 2;
/** The innermost and outermost orbit radii — the reference's own. */
export const INNER = 54;
export const OUTER = 138;
/** Where the disc ends: the wedges and the rim run to here. */
export const RIM = OUTER + 8;
/** The unplaced drift beyond the rim, where an author has not put them yet. */
export const DRIFT = RIM + 16;
/** The top gap holds the gate label; the members take the rest, clockwise. */
export const GATE = { a0: -74, span: 328 } as const;
/** How far along its orbit two neighbours must be, in field units. */
const MIN_SEPARATION = 34;
const SECTOR_PAD_DEG = 7;

export type Camera = { s: number; tx: number; ty: number };
export type Span = { a0: number; a1: number };
export type Point = [number, number];

/** A ring's orbit radius by SLOT (its drawn order), not its authored level. */
export const ringRadius = (slot: number, count: number) =>
  count <= 1 ? (INNER + OUTER) / 2 : INNER + ((OUTER - INNER) * slot) / (count - 1);

export const polar = (r: number, deg: number): Point => [
  CENTRE + r * Math.cos((deg * Math.PI) / 180),
  CENTRE + r * Math.sin((deg * Math.PI) / 180),
];

export type LayoutRing = { id: string; planets: string[] };
export type LayoutWorld = { id: string; ring: string | null; sector: string; sun: boolean };

export type SkyLayout = {
  /** Ring id → slot (0 innermost). */
  slots: Map<string, number>;
  radii: number[];
  /** Sector id → wedge, in degrees.  "" is the unfiled wedge, present only when needed. */
  spans: Map<string, Span>;
  /** Sector ids in wedge order. */
  sectors: string[];
  positions: Map<string, Point>;
  /** Every placed, non-sun world's angle on its ring, in degrees. */
  angles: Map<string, number>;
  /** The sun's id, or null when no world is flagged. */
  sun: string | null;
  /** Planets on no ring, in the order they drift. */
  unplaced: string[];
};

/**
 * Cut the disc.  A wedge's weight is the room its tightest ring needs —
 * members × minimum separation over that ring's radius — plus a pad, so
 * a family with three worlds on the innermost ring gets the angle those
 * three need there, whatever its outer rings hold.
 */
function sectorSpans(
  sectors: string[],
  rings: LayoutRing[],
  radii: number[],
  worlds: Map<string, LayoutWorld>,
): Map<string, Span> {
  const pad = (SECTOR_PAD_DEG * Math.PI) / 180;
  const weights = sectors.map((sector) => {
    let need = 0;
    rings.forEach((ring, slot) => {
      const members = ring.planets.filter((id) => {
        const world = worlds.get(id);
        return world && !world.sun && world.sector === sector;
      }).length;
      need = Math.max(need, (members * MIN_SEPARATION) / radii[slot]);
    });
    return need + pad;
  });
  const total = weights.reduce((sum, weight) => sum + weight, 0) || 1;
  const scale = ((GATE.span * Math.PI) / 180) / total;
  const out = new Map<string, Span>();
  let cursor = (GATE.a0 * Math.PI) / 180;
  sectors.forEach((sector, index) => {
    const width = weights[index] * scale;
    out.set(sector, { a0: (cursor * 180) / Math.PI, a1: ((cursor + width) * 180) / Math.PI });
    cursor += width;
  });
  return out;
}

/**
 * Lay the sky out.  `sectorOrder` is the closed vocabulary in the order
 * the wedges are cut; a world filed under no family, or under one the
 * order does not name, lands in a trailing "unfiled" wedge that exists
 * only while something needs it.
 */
export function layoutSky(
  rings: LayoutRing[],
  worlds: LayoutWorld[],
  unplaced: string[],
  sectorOrder: string[],
): SkyLayout {
  const byId = new Map(worlds.map((world) => [world.id, world]));
  const slots = new Map<string, number>();
  rings.forEach((ring, slot) => slots.set(ring.id, slot));
  const radii = rings.map((_, slot) => ringRadius(slot, rings.length));

  const known = new Set(sectorOrder);
  const strays = worlds.some((world) => !world.sun && world.ring !== null && !known.has(world.sector));
  const sectors = strays ? [...sectorOrder, ""] : [...sectorOrder];
  const sectorOf = (world: LayoutWorld) => (known.has(world.sector) ? world.sector : "");

  const spans = sectorSpans(sectors, rings, radii, new Map(
    worlds.map((world) => [world.id, { ...world, sector: sectorOf(world) }]),
  ));

  const positions = new Map<string, Point>();
  const angles = new Map<string, number>();
  const sun = worlds.find((world) => world.sun)?.id ?? null;
  if (sun) positions.set(sun, [CENTRE, CENTRE]);

  rings.forEach((ring, slot) => {
    const r = radii[slot];
    sectors.forEach((sector) => {
      const span = spans.get(sector);
      if (!span) return;
      const members = ring.planets.filter((id) => {
        const world = byId.get(id);
        return world && !world.sun && sectorOf(world) === sector;
      });
      members.forEach((id, index) => {
        const angle = span.a0 + ((span.a1 - span.a0) * (index + 0.5)) / members.length;
        angles.set(id, angle);
        positions.set(id, polar(r, angle));
      });
    });
  });

  unplaced.forEach((id, index) => {
    const angle = GATE.a0 + (GATE.span * (index + 0.5)) / Math.max(1, unplaced.length);
    angles.set(id, angle);
    positions.set(id, polar(DRIFT, angle));
  });

  return { slots, radii, spans, sectors, positions, angles, sun, unplaced: [...unplaced] };
}

/**
 * Where a sector's rim label sits: the middle of the widest gap between
 * the outermost ring's members of that family, so the name never lands
 * on top of a world, clamped so a long name stays inside its wedge.
 */
export function sectorLabelAngle(
  span: Span,
  outerMemberAngles: number[],
  halfWidthDeg: number,
): number {
  const marks = [span.a0, ...[...outerMemberAngles].sort((a, b) => a - b), span.a1];
  let best = (span.a0 + span.a1) / 2;
  let gap = -1;
  for (let i = 0; i + 1 < marks.length; i += 1) {
    const width = marks[i + 1] - marks[i];
    if (width > gap) {
      gap = width;
      best = (marks[i] + marks[i + 1]) / 2;
    }
  }
  const lo = span.a0 + halfWidthDeg + 1;
  const hi = span.a1 - halfWidthDeg - 1;
  return hi <= lo ? (span.a0 + span.a1) / 2 : Math.max(lo, Math.min(hi, best));
}

/** The SVG path of one wedge between two radii. */
export function wedgePath(span: Span, ra: number, rb: number): string {
  const [ax0, ay0] = polar(ra, span.a0);
  const [bx0, by0] = polar(rb, span.a0);
  const [bx1, by1] = polar(rb, span.a1);
  const [ax1, ay1] = polar(ra, span.a1);
  const big = span.a1 - span.a0 > 180 ? 1 : 0;
  return (
    `M ${ax0.toFixed(2)} ${ay0.toFixed(2)} L ${bx0.toFixed(2)} ${by0.toFixed(2)}` +
    ` A ${rb} ${rb} 0 ${big} 1 ${bx1.toFixed(2)} ${by1.toFixed(2)}` +
    ` L ${ax1.toFixed(2)} ${ay1.toFixed(2)} A ${ra} ${ra} 0 ${big} 0 ${ax0.toFixed(2)} ${ay0.toFixed(2)} Z`
  );
}

// ── the cameras ───────────────────────────────────────────────────────
//
// "Sky", "ring", "sector" and "world" are one drawing seen through four
// cameras — a scale and a translate on one group — which is what makes
// zooming feel like flying rather than navigating.

const clamp = (lo: number, hi: number, x: number) => Math.max(lo, Math.min(hi, x));

export const SKY_CAMERA: Camera = { s: 1, tx: 0, ty: 0 };

/** A ring is framed to leave room for its names, not to fill the glass. */
export function ringCamera(slot: number, count: number): Camera {
  const s = clamp(0.86, 1.35, 292 / (2 * (ringRadius(slot, count) + 42)));
  return { s, tx: CENTRE - CENTRE * s, ty: CENTRE - CENTRE * s };
}

/**
 * A column is framed to its own bounds, with room for the names around it.
 *
 * Deliberately not tight.  A wedge of five worlds would fill the glass at
 * the ceiling below, which leaves its names nowhere to stand and hides
 * the thing a column is FOR — seeing one family against the rest of the
 * sky.  So it stops at half again life size and lets the neighbours show,
 * dimmed, around it.
 */
export function sectorCamera(members: Point[]): Camera {
  if (!members.length) return SKY_CAMERA;
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  members.forEach(([x, y]) => {
    x0 = Math.min(x0, x);
    y0 = Math.min(y0, y);
    x1 = Math.max(x1, x);
    y1 = Math.max(y1, y);
  });
  const pad = 44;
  x0 -= pad;
  y0 -= pad;
  x1 += pad;
  y1 += pad;
  const s = clamp(0.72, 1.5, Math.min(338 / (x1 - x0), 338 / (y1 - y0)));
  return { s, tx: CENTRE - ((x0 + x1) / 2) * s, ty: CENTRE - ((y0 + y1) / 2) * s };
}

/** A world fills the glass. */
export function worldCamera(at: Point): Camera {
  const s = 3.4;
  return { s, tx: CENTRE - at[0] * s, ty: CENTRE - at[1] * s };
}

/** The frame, in field units, as seen through a camera. */
export function frameUnder(cam: Camera): { x0: number; y0: number; x1: number; y1: number } {
  return {
    x0: (2 - cam.tx) / cam.s,
    x1: (FIELD - 2 - cam.tx) / cam.s,
    y0: (3 - cam.ty) / cam.s,
    y1: (FIELD - 3 - cam.ty) / cam.s,
  };
}
